import { Modal, Box, Button, Backdrop } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { postClassIdsAndRoles, updateClassIdsEdit } from "../../../../components/Api/Post";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMutation } from "react-query";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import isAuthenticated from "../../../../components/Utils/isAuthenticated";
import { selectedClassAtom } from "../../../../components/Atoms/atoms";
import { useAtom } from "jotai";
import FacultyRankAutocomplete from "../../../../components/Utils/FacultyRankAutocomplete";
import ListSelect from "../../../../components/Utils/ListSelect";
import {
	useFacultyRank,
	useAddInstructorToFacultyRank,
} from "../../../../Hooks/useFacultyRankQuery";
import { DeleteForever } from "@mui/icons-material";
import RolePicker from "../../../SPOTSurveys/SPOTOrdering/components/RolePicker";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';


const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "80svw",
	height: "95svh",
	bgcolor: "primary.contrastText",

	borderRadius: 2,
	display: "flex",
	flexDirection: "column",
};

export default function EditClassModal(props) {
	const { deptId, handleRefresh, open, setOpen, classIds } = props;

	const isInInstance = false;

	const auth = isAuthenticated();
	const token = auth.token;
	const [selectedClass, setSelectedClass] = useAtom(selectedClassAtom);
	const [, setExistingRoles] = useState([]);
	const [originalClass, setOriginalClass] = useState(null);
	const [instructorRole, setInstructorRole] = useState("");
	const [openInstructorDialog, setOpenInstructorDialog] = useState(false);
	const [newRoleValue, setNewRoleValue] = useState("");
	const [newlyAddedToFacultyRank, setNewlyAddedToFacultyRank] = useState(false);
	const [hasAddedInstructor, setHasAddedInstructor] = useState(false);
	const [addInstructor, setAddInstructor] = useState(false);
	const [combineSelection, setCombineSelection] = useState([]);
	const [combineFilter, setCombineFilter] = useState("");
	const [combineError, setCombineError] = useState(null);
	const [hasForcedChildren, setHasForcedChildren] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const classRoles = ["PI", "TA", "CC", "CO", "LA", "GL", "AI", "PG", "SG", "TC", "DA"];
	const rolesDescriptions = {
		PI: "Primary Instructor",
		TA: "Teaching Assistant",
		CC: "Course Coordinator",
		CO: "Co-Instructor",
		LA: "Lab Assistant",
		GL: "Grader/Lab Assistant",
		AI: "Assistant Instructor",
		PG: "Primary Grader",
		SG: "Secondary Grader",
		TC: "Teaching Coordinator",
		DA: "Department Assistant",
	};

	const { data: facultyRank } = useFacultyRank(token);

	const { addInstructorToFacultyRank } = useAddInstructorToFacultyRank(token, deptId);
	useEffect(() => {
		if (open && selectedClass) {
			// parse existing combine string from selectedClass
			const raw = selectedClass.combine;
			if (raw && String(raw).trim()) {
				const parts = String(raw)
					.split(",")
					.map((p) => p.trim())
					.filter(Boolean);

				const ids = parts
					.map((p) => {
						const n = Number(p);
						return Number.isFinite(n) ? n : null;
					})
					.filter((n) => n !== null);

				setCombineSelection(ids);
			} else {
				setCombineSelection([]);
			}
			setCombineError(null);
		}
		if (!open) {
			setExpanded(null);
			setCombineSelection([]);
			setCombineError(null);
		}
	}, [open, selectedClass?.classid]);
	const combineCandidates = useMemo(() => {
		if (!classIds || !selectedClass) return [];

		return classIds
			.filter((ci) => {
				if (ci.classid === selectedClass.classid) return false;
				if (!ci.csize || ci.csize <= 0) return false;
				// Optionally block SIS-precombined children from being used as combine targets
				if (ci.csPreCombined) return false;

				return true;
			})
			.sort((a, b) => a.classid - b.classid);
	}, [classIds, selectedClass]);
	const filteredCombineCandidates = useMemo(() => {
		if (!combineFilter.trim()) return combineCandidates;

		const q = combineFilter.toLowerCase();
		return combineCandidates.filter((ci) => {
			const title = (ci.ctitle || "").toLowerCase();
			const subj = (ci.subject_id || "").toLowerCase();
			const cnum = (ci.cnumber || "").toLowerCase();
			const cidStr = String(ci.classid);

			return title.includes(q) || subj.includes(q) || cnum.includes(q) || cidStr.includes(q);
		});
	}, [combineFilter, combineCandidates]);
	const toggleCombineClass = (classid) => {
		setCombineSelection((prev) =>
			prev.includes(classid) ? prev.filter((id) => id !== classid) : [...prev, classid],
		);
	};
	const getInstructorKey = (ci) => {
		const roles = ci.roles || [];
		const normalized = roles
			.filter((r) => r.empid) // just in case
			.map((r) => ({
				empid: String(r.empid),
				class_role: r.class_role || "",
			}))
			.sort((a, b) => {
				if (a.empid !== b.empid) return a.empid.localeCompare(b.empid);
				return a.class_role.localeCompare(b.class_role);
			});

		return JSON.stringify(normalized);
	};

	const handleApplyCombine = () => {
		if (!selectedClass) return;

		setCombineError(null);

		// If nothing selected -> treat as "uncombine"
		if (combineSelection.length === 0) {
			const restoredCsize =
				originalClass && originalClass.classid === selectedClass.classid
					? originalClass.csize
					: selectedClass.csize;

			const updated = {
				...selectedClass,
				combine: "",
				csize: restoredCsize,
			};
			setSelectedClass(updated);
			setExpanded(null);
			return;
		}

		// Build a lookup for quick access
		const byId = {};
		classIds?.forEach((ci) => {
			byId[ci.classid] = ci;
		});

		const baseKey = getInstructorKey(selectedClass);
		const badIds = [];

		for (const cid of combineSelection) {
			const target = byId[cid];
			if (!target) continue;
			const key = getInstructorKey(target);
			if (key !== baseKey) {
				badIds.push(cid);
			}
		}

		if (badIds.length > 0) {
			setCombineError(
				`These classes do not have the same instructor set and cannot be combined: ${badIds.join(
					", ",
				)}`,
			);
			return;
		}

		// Everything passes: compute new combine string and csize
		const newCombine = combineSelection.join(",");

		// Use originalClass.csize as the "base" so re-combining doesn't double-count
		const baseSize =
			originalClass && originalClass.classid === selectedClass.classid
				? originalClass.csize || 0
				: selectedClass.csize || 0;

		let extraSize = 0;
		for (const cid of combineSelection) {
			const target = byId[cid];
			if (target && target.csize) extraSize += target.csize;
		}

		const updated = {
			...selectedClass,
			combine: newCombine,
			csize: baseSize + extraSize,
		};

		setSelectedClass(updated);
		setExpanded(null);
	};

	useEffect(() => {
		if (open && selectedClass && !originalClass) {
			setOriginalClass(JSON.parse(JSON.stringify(selectedClass)));
			setHasForcedChildren(false);
		}
		if (!open) {
			setOriginalClass(null);
			setHasForcedChildren(false);
		}
	}, [open, selectedClass, originalClass]);

	// Auto-update ctitle when combineSelection changes
	useEffect(() => {
		if (!originalClass || !classIds) return;

		// Helper to parse ctitle into its 4 parts: SUBJECT_ID CNUMBER CTYPE SECTION
		// e.g., "NURS 325 LAB H104" -> { subject_id: "NURS", cnumber: "325", ctype: "LAB", section: "H104" }
		const parseCtitle = (ctitle) => {
			if (!ctitle) return { subject_id: "", cnumber: "", ctype: "", section: "" };
			const parts = ctitle.trim().split(/\s+/);
			return {
				subject_id: parts[0] || "",
				cnumber: parts[1] || "",
				ctype: parts[2] || "",
				section: parts[3] || "",
			};
		};

		// Helper to combine ctitles - root parts come first, then unique parts from combined courses
		const combineCtitles = (rootCtitle, combinedCtitles) => {
			const root = parseCtitle(rootCtitle);
			const combined = combinedCtitles.map(parseCtitle);

			const buildPart = (key) => {
				const values = [root[key]];
				combined.forEach((c) => {
					if (c[key] && !values.includes(c[key])) {
						values.push(c[key]);
					}
				});
				return values.join("/");
			};

			return `${buildPart("subject_id")} ${buildPart("cnumber")} ${buildPart("ctype")} ${buildPart("section")}`;
		};

		let newCtitle;
		if (combineSelection.length === 0) {
			// Revert to original ctitle when no combinations selected
			newCtitle = originalClass.ctitle;
		} else {
			// Get ctitles of combined classes
			const combinedCtitles = combineSelection
				.map((cid) => classIds.find((ci) => ci.classid === cid))
				.filter(Boolean)
				.map((ci) => ci.ctitle);

			newCtitle = combineCtitles(originalClass.ctitle, combinedCtitles);
		}

		setSelectedClass((prev) => {
			if (!prev || prev.ctitle === newCtitle) return prev;
			return { ...prev, ctitle: newCtitle };
		});
	}, [combineSelection, classIds, originalClass]);

	const hasChanges = useMemo(() => {
		if (!originalClass || !selectedClass) return false;

		// Forcing instructors onto children is a change we care about
		if (hasForcedChildren) return true;

		// Compare simple fields, now including combine + csize
		const fieldsToCompare = [
			"ctitle",
			"c_sdate",
			"c_edate",
			"original",
			"teamsize",
			"combine",
			"csize",
		];

		for (const field of fieldsToCompare) {
			if (originalClass[field] !== selectedClass[field]) {
				return true;
			}
		}

		// Compare roles array
		if (originalClass.roles?.length !== selectedClass.roles?.length) {
			return true;
		}

		if (originalClass.roles && selectedClass.roles) {
			// Check if any role was modified (by empid and class_role)
			for (let i = 0; i < originalClass.roles.length; i++) {
				const origRole = originalClass.roles[i];
				const currRole = selectedClass.roles.find((r) => r.empid === origRole.empid);
				if (!currRole || currRole.class_role !== origRole.class_role) {
					return true;
				}
			}
			// Check for new roles added
			for (let i = 0; i < selectedClass.roles.length; i++) {
				const currRole = selectedClass.roles[i];
				const origRole = originalClass.roles.find((r) => r.empid === currRole.empid);
				if (!origRole) {
					return true;
				}
			}
		}

		return false;
	}, [originalClass, selectedClass, hasForcedChildren]);

	const postClassIdsRolesMutation = useMutation(({ token, listOfNewRows }) =>
		postClassIdsAndRoles({ token, listOfNewRows }),
	);

	const checkExistingFacultyRank = (empid) => {
		let exists = false;
		facultyRank.forEach((rank) => {
			if (Number(rank.empid) === Number(empid)) {
				exists = true;
			}
		});
		return exists;
	};

	const handleAddInstructor = async () => {
		const instructorToAdd = newRoleValue;

		let newRole = {
			ccid: instructorToAdd.ccid,
			class_role: instructorRole,

			checked: false,
			classid: selectedClass.classid,
			disabled: false,
			empid: instructorToAdd.empid,
			name: instructorToAdd.name,
			first_name: instructorToAdd.name.split(", ")[0],
			last_name: instructorToAdd.name.split(", ")[1],
			role_description: rolesDescriptions[instructorRole],
			roleid: Number(instructorToAdd.empid) + selectedClass.classid,
			term: selectedClass.term,
			import_status: "MANUAL",
		};

		// Create a new array instance to trigger re-render

		//update selectedClass
		const updatedSelectedClass = { ...selectedClass };
		updatedSelectedClass.roles = [...updatedSelectedClass.roles, newRole];
		updatedSelectedClass.teamsize = updatedSelectedClass.roles.length;
		updatedSelectedClass.addedNewInstructor = true;
		setSelectedClass(updatedSelectedClass);
		setHasAddedInstructor(true);
		if (!isInInstance) setAddInstructor(false);

		setInstructorRole("");
	};

	const handleAddInstructorToFacultyRank = async (instructorToAdd) => {
		const success = await addInstructorToFacultyRank(instructorToAdd);
		return success;
	};

	const handleChangeRole = (event) => {
		setInstructorRole(event.target.value);
	};

	const handleCancelAddInstructor = () => {
		setNewRoleValue("");
		setNewlyAddedToFacultyRank(null);
		setOpenInstructorDialog(false);
		setInstructorRole("");
		setHasAddedInstructor(false);
		setAddInstructor(false);
	};

	// const isDateInCorrectFormat = (dateString) => {
	// 	// Regex to match YYYY-MM-DD format
	// 	const regex = /^\d{4}-\d{2}-\d{2}$/;
	// 	if (!regex.test(dateString)) return false;
	// 	return true;
	// };

	// const isCombineClassesCorrect = (combClassesString) => {
	// 	// Allow empty string
	// 	if (typeof combClassesString === "object") {
	// 		if (combClassesString.length === 0) return true;
	// 		else if (combClassesString.every((string) => Number(string).length === 5)) return true;
	// 		return false;
	// 	}
	// 	if (typeof combClassesString !== "string") {
	// 		if (!combClassesString || combClassesString?.trim() === "") return true;
	// 		if (combClassesString?.split(",").every((string) => Number(string).length === 5)) return true;
	// 		return false;
	// 	}
	// };

	// const canSubmit =
	// 	selectedClass?.roles.length > 0 &&
	// 	selectedClass?.ctitle.split(" ").length >= 4 &&
	// 	isDateInCorrectFormat(selectedClass?.c_sdate) &&
	// 	isDateInCorrectFormat(selectedClass?.c_edate) &&
	// 	isCombineClassesCorrect(selectedClass?.combine) &&
	// 	(selectedClass?.original?.length === 5 ||
	// 		selectedClass?.original === "" ||
	// 		selectedClass?.original === null);

	// const handleSubmit = async () => {
	// 	postClassIdsRolesMutation.mutateAsync(
	// 		{ token, listOfNewRows: [selectedClass] },
	// 		{
	// 			onSuccess: (data, error) => {
	// 				handleRefresh();
	// 				setOpen(false);
	// 			},
	// 		}
	// 	);

	// 	setOpen(false);
	// };

	const handleClose = (event, reason) => {
		if (reason && reason === "backdropClick") {
			return;
		} else {
			setAddInstructor(false);
			setOriginalClass(null);
			setOpen(false);
		}
	};

	const [loading, setLoading] = useState(false);

	// Handle saving changes
	const handleSaveChanges = async () => {
		// TODO: Replace with actual API call to update the class
		// For now, using the existing mutation as a placeholder

		console.log("test");

		try {
			await updateClassIdsEdit(token, selectedClass);
			setOriginalClass(JSON.parse(JSON.stringify(selectedClass)));

			handleRefresh();
			setLoading(false);
			setOpen(false);
		} catch (error) {
			console.error("Failed to save changes:", error);
			setLoading(false);
			alert("Failed to save changes. Please try again.");
		}
	};

	// Discard changes and revert to original
	const handleDiscardChanges = () => {
		if (originalClass) {
			setSelectedClass(JSON.parse(JSON.stringify(originalClass)));
		}
	};

	const handleEditClass = (e) => {
		const { name, value } = e.target;
		const updatedSelectedClass = { ...selectedClass };
		updatedSelectedClass[name] = value;
		setSelectedClass(updatedSelectedClass);
	};

	const deleteInstructor = (event, instructor) => {
		const updatedSelectedClass = { ...selectedClass };
		updatedSelectedClass.roles = updatedSelectedClass.roles.filter(
			(role) => role.empid !== instructor.empid,
		);
		updatedSelectedClass.teamsize = updatedSelectedClass.roles.length;
		setSelectedClass(updatedSelectedClass);
	};

	const handleForceInstructorsToChildren = () => {
		if (!selectedClass || !classIds || combineSelection.length === 0) return;

		const baseRoles = selectedClass.roles || [];
		const clonedBaseRoles = baseRoles.map((r) => ({ ...r }));

		combineSelection.forEach((cid) => {
			const targetClass = classIds.find((ci) => ci.classid === cid);
			if (targetClass) {
				// mutate roles in place so getInstructorKey() sees the new set
				targetClass.roles = clonedBaseRoles.map((r) => ({ ...r }));
				targetClass.teamsize = clonedBaseRoles.length;
			}
		});

		// Clear the error so they can re-apply combine
		setCombineError(null);
		setHasForcedChildren(true);
	};

	const handleChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	return (
		<div>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={style}>
					{/* Loading overlay for saving */}
					<Backdrop
						open={loading}
						sx={{
							color: "#fff",
							zIndex: (theme) => theme.zIndex.modal + 1,
							position: "absolute",
							borderRadius: 2,
						}}
					>
						<div className="flex flex-col items-center gap-3">
							<CircularProgress color="inherit" />
							<span className="text-white font-medium">Saving changes...</span>
						</div>
					</Backdrop>

					<div className="w-full h-full p-2 flex flex-col items-center pt-2">
						<div className="flex flex-col  w-full h-full overflow-auto">
							<div className=" bg-digitalWhite rounded p-2">
								<div className="grid grid-cols-4  rounded w-full">
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="ctitle">Course Title:</label>
										<input
											type="text"
											name="ctitle"
											value={selectedClass?.ctitle || ""}
											onChange={handleEditClass}
											className="border-2 rounded-md p-1 disabled:text-gray-500 disabled:cursor-not-allowed"
										/>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="classid">Class ID:</label>
										<input
											type="text"
											disabled
											name="classid"
											value={selectedClass?.classid || ""}
											onChange={handleEditClass}
											className="border-2 rounded-md p-1 disabled:text-gray-500 disabled:cursor-not-allowed"
										/>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="c_sdate">Course Start Date:</label>
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DatePicker
												value={selectedClass?.c_sdate ? dayjs(selectedClass.c_sdate) : null}
												onChange={(newValue) => {
													const formatted = newValue ? newValue.format("YYYY-MM-DD") : "";
													handleEditClass({ target: { name: "c_sdate", value: formatted } });
												}}
												slotProps={{
													textField: {
														size: "small",
														sx: { backgroundColor: "white" },
													},
												}}
											/>
										</LocalizationProvider>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="c_edate">Course End Date:</label>
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DatePicker
												value={selectedClass?.c_edate ? dayjs(selectedClass.c_edate) : null}
												onChange={(newValue) => {
													const formatted = newValue ? newValue.format("YYYY-MM-DD") : "";
													handleEditClass({ target: { name: "c_edate", value: formatted } });
												}}
												slotProps={{
													textField: {
														size: "small",
														sx: { backgroundColor: "white" },
													},
												}}
											/>
										</LocalizationProvider>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="teamsize">Class Size:</label>
										<input
											type="text"
											name="csize"
											disabled
											value={selectedClass?.csize || ""}
											onChange={handleEditClass}
											className="border-2 rounded-md p-1 disabled:text-gray-500 disabled:cursor-not-allowed"
										/>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="teamsize">Team Size:</label>
										<input
											type="text"
											disabled
											name="teamsize"
											value={selectedClass?.teamsize || ""}
											onChange={handleEditClass}
											className="border-2 rounded-md p-1 disabled:text-gray-500 disabled:cursor-not-allowed"
										/>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="combine">Combined With:</label>
										<div className="relative group">
											<div className="border-2 rounded-md p-1 bg-gray-100 text-gray-500 cursor-help min-h-[34px] text-sm flex items-center">
												{selectedClass?.combine || <span className="italic">None</span>}
											</div>
											{selectedClass?.combine && (
												<div className="absolute z-50 hidden group-hover:block top-full left-0 mb-2 w-80 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3">
													<div className="font-semibold mb-2 border-b border-gray-700 pb-1">
														Combined Classes:
													</div>
													<div className="space-y-2 max-h-48 overflow-y-auto">
														{String(selectedClass.combine)
															.split(",")
															.map((cid) => cid.trim())
															.filter(Boolean)
															.map((cid) => {
																const combinedClass = classIds?.find(
																	(ci) => ci.classid === Number(cid),
																);
																if (!combinedClass) {
																	return (
																		<div key={cid} className="text-gray-400">
																			{cid} - (Not found)
																		</div>
																	);
																}
																return (
																	<div
																		key={cid}
																		className="border-b border-gray-700 pb-1 last:border-0"
																	>
																		<div className="font-medium">{combinedClass.ctitle}</div>
																		<div className="text-gray-300">
																			ID: {combinedClass.classid} | Size: {combinedClass.csize || 0}
																		</div>
																		<div className="text-gray-400 truncate">
																			{combinedClass.roles
																				?.map(
																					(r) =>
																						r.name || `${r.user?.last_name}, ${r.user?.first_name}`,
																				)
																				.join(", ") || "No instructors"}
																		</div>
																	</div>
																);
															})}
													</div>
													<div className="absolute left-4 bottom-0 translate-y-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-900"></div>
												</div>
											)}
										</div>
									</div>
									<div className="grid grid-rows-2   items-center p-2">
										<label htmlFor="original">Instance Original:</label>
										<input
											type="text"
											disabled
											name="original"
											value={selectedClass?.original || ""}
											onChange={handleEditClass}
											className="border-2 rounded-md p-1 disabled:text-gray-500 disabled:cursor-not-allowed"
										/>
									</div>
								</div>
							</div>
							<Accordion
								expanded={expanded === "instructors"}
								onChange={handleChange("instructors")}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls="panel1-content"
									id="panel1-header"
								>
									Edit Instructors ({selectedClass?.teamsize})
								</AccordionSummary>
								<AccordionDetails className="flex flex-col gap-4 bg-white">
									{/* Add Instructor Section */}

									{/* Instructors Table */}
									<div className="w-full border rounded-md  max-h-[35dvh] overflow-y-auto">
										<table className="w-full text-sm">
											<thead className="bg-gray-100">
												<tr>
													<th className="p-3 text-left font-semibold">Name</th>
													<th className="p-3 text-left font-semibold w-40">Role</th>
													<th className="p-3 text-center font-semibold w-24">Actions</th>
												</tr>
											</thead>
											<tbody>
												{selectedClass?.roles.length === 0 && (
													<tr>
														<td colSpan={3} className="p-4 text-center text-gray-500">
															No instructors assigned to this class.
														</td>
													</tr>
												)}
												{selectedClass?.roles.map((role) => (
													<tr key={role.empid} className="border-t hover:bg-gray-50">
														<td className="p-3">
															<span
																title={
																	role?.name
																		? role.name
																		: role?.user?.first_name
																				.concat(" ")
																				.concat(role?.user?.last_name)
																}
															>
																{role.name
																	? role.name
																	: `${role?.user?.last_name}, ${role?.user?.first_name}`}
															</span>
														</td>
														<td className="p-3">
															<RolePicker
																list={classRoles}
																label="Role"
																className="w-full"
																size="small"
																marginBottom="0px"
																value={role.class_role || ""}
																onChange={(event) => handleChangeRole(event, role, selectedClass)}
															/>
														</td>
														<td className="p-3 text-center">
															<button
																onClick={(event) => deleteInstructor(event, role)}
																className="bg-red-700 rounded-md p-1 hover:bg-red-500 text-white"
																title="Remove instructor"
															>
																<PersonRemoveIcon fontSize="small" />
															</button>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
									{addInstructor ? (
										<div className="flex items-center gap-2 p-3 bg-white rounded-md border">
											<span className="text-sm font-medium text-gray-600 whitespace-nowrap">
												Add:
											</span>
											<FacultyRankAutocomplete
												options={facultyRank}
												currentRow={selectedClass}
												setExistingRoles={setExistingRoles}
												isInInstance={isInInstance}
												newRoleValue={newRoleValue}
												setNewRoleValue={setNewRoleValue}
												checkExistingFacultyRank={checkExistingFacultyRank}
												addInstructorToFacultyRank={handleAddInstructorToFacultyRank}
												isInEditClasses
											/>
											<ListSelect
												required
												marginBottom="0"
												label="Role"
												value={instructorRole}
												onChange={handleChangeRole}
												list={classRoles}
												width={100}
											/>
											<Button
												variant="contained"
												disabled={!newRoleValue || !instructorRole}
												onClick={handleAddInstructor}
												sx={{ minWidth: "80px" }}
											>
												Add
											</Button>
											<Button
												color="error"
												variant="outlined"
												onClick={handleCancelAddInstructor}
												sx={{ minWidth: "80px" }}
											>
												Cancel
											</Button>
										</div>
									) : (
										<button
											onClick={() => setAddInstructor(true)}
											className="self-end shadow-md hover:ring-2 hover:ring-gray-300 border-2 rounded-md px-4 py-2 border-green-700 bg-green-700 text-gray-100 text-sm font-medium hover:bg-green-800 hover:border-green-800 transition-colors"
										>
											<PersonAddIcon /> Add Instructor
										</button>
									)}
								</AccordionDetails>
								{openInstructorDialog && (
									<div className="min-w-[500px] p-4 border-2 rounded-md shadow-md flex flex-col w-full gap-2">
										<p className="text-center max-w-[600px]">
											Please search for and add instructors. You can add multiple to the same
											instanced class. Press "Done" when you have finished adding instructors for
											each instance.
										</p>
										<div>
											{newlyAddedToFacultyRank ? (
												<div className="flex  w-full items-center gap-2 py-2">
													<div className=" rounded-md p-4">Currently Adding:</div>
													<div className="border-2 border-gray-300 text-primary font-semibold rounded-md p-4">
														{newlyAddedToFacultyRank.name} - {newlyAddedToFacultyRank.empid}
													</div>
													<ListSelect
														required
														marginBottom="0"
														label="Role"
														error={newlyAddedToFacultyRank && !instructorRole}
														value={instructorRole}
														onChange={handleChangeRole}
														list={classRoles}
														width={100}
													/>
												</div>
											) : (
												<div className="flex  w-full items-center gap-2 py-2">
													<FacultyRankAutocomplete
														options={facultyRank}
														currentRow={selectedClass}
														isInInstance
														newRoleValue={newRoleValue}
														setNewRoleValue={setNewRoleValue}
														setNewlyAddedToFacultyRank={setNewlyAddedToFacultyRank}
														setExistingRoles={() => {}}
														checkExistingFacultyRank={checkExistingFacultyRank}
														addInstructorToFacultyRank={addInstructorToFacultyRank}
													/>
													<ListSelect
														required
														marginBottom="0"
														label="Role"
														error={newRoleValue && !instructorRole}
														value={instructorRole}
														onChange={handleChangeRole}
														list={classRoles}
														width={100}
													/>
												</div>
											)}

											<div className="flex items-center w-full gap-2">
												<Button
													sx={{ width: "100%" }}
													color="error"
													variant="contained"
													onClick={handleCancelAddInstructor}
												>
													Cancel
												</Button>

												<Button
													variant="outlined"
													disabled={!hasAddedInstructor}
													sx={{ width: "100%" }}
													onClick={() => {
														setOpenInstructorDialog(false);
													}}
												>
													Done
												</Button>

												<Button
													variant="contained"
													disabled={
														// if no instructor role, and if either no newRoleValue or newlyAddedToFacultyRank
														!instructorRole || (!newRoleValue && !newlyAddedToFacultyRank)
													}
													// onClick={handleAddInstructorToFacultyRank}
													sx={{ width: "100%" }}
												>
													Add to Course
												</Button>
											</div>
										</div>
									</div>
								)}
								{/* </div> */}
							</Accordion>
							<Accordion expanded={expanded === "combines"} onChange={handleChange("combines")}>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls="panel1-content"
									id="panel1-header"
								>
									Combine Class
								</AccordionSummary>
								<AccordionDetails>
									{selectedClass?.combine && selectedClass?.combine.length > 0 && (
										<div className=" flex gap-4 mb-2">
											<input
												type="text"
												disabled
												name="combine"
												value={selectedClass?.combine || ""}
												onChange={handleEditClass}
												className="border-2 rounded-md p-1 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
											/>
											{selectedClass?.csPreCombined && (
												<p className="text-sm italic self-center">
													* Combined From BearTracks (SIS)
												</p>
											)}
										</div>
									)}

									{/* COMBINE EDITOR */}
									{expanded === "combines" && !selectedClass?.csPreCombined && (
										<div className="mt-4 border rounded-md p-3 bg-gray-50 flex flex-col gap-2">
											<div className="flex gap-2 w-full ">
												<div className="flex flex-col gap-2 ">
													<div className="flex flex-col gap-2 ">
														<p className="text-sm text-gray-700">
															Select other classes to combine with{" "}
															<strong>{selectedClass.classid}</strong>. All selected classes must:
														</p>
														<ul className="list-disc ml-5 text-sm text-gray-700">
															<li className="max-w-[60ch]">
																Have the same instructor <strong>set</strong> as this class (
																{selectedClass?.roles
																	.map((instructor) => instructor.name)
																	.join(" | ")}
																) (ALL instructors must match)
															</li>
														</ul>
													</div>

													{/* Summary & errors */}
													<div className="flex justify-between flex-col items-start  ">
														<div className="text-sm text-gray-700">
															<span className="font-semibold">
																Selected classes: {combineSelection.length}
															</span>
															{selectedClass && (
																<>
																	{" "}
																	<span className="ml-2">
																		Base size: {originalClass?.csize ?? selectedClass.csize}
																	</span>
																</>
															)}
														</div>

														{combineError && (
															<>
																<p className="text-xs text-red-600  ">{combineError}</p>
																<button
																	className="text-xs text-primary underline"
																	onClick={handleForceInstructorsToChildren}
																>
																	Force Instructors From Root to Combined Classes
																</button>
															</>
														)}
													</div>
												</div>
												<div className="  flex flex-col gap-2 w-full">
													<div className="flex gap-2 items-center">
														<input
															type="text"
															placeholder="Filter by class ID, subject, number, or title..."
															value={combineFilter}
															onChange={(e) => setCombineFilter(e.target.value)}
															className="border rounded-md p-2 flex-1 text-sm"
														/>
													</div>

													<div className="max-h-64 overflow-y-auto border rounded-md bg-white">
														<table className="w-full text-sm ">
															<thead className="bg-gray-100 sticky top-0">
																<tr>
																	<th className="p-2 text-left w-10"></th>
																	<th className="p-2 text-left">Class ID</th>
																	<th className="p-2 text-left">Course</th>

																	<th className="p-2 text-right">Size</th>
																	<th className="p-2 text-right">Name</th>
																</tr>
															</thead>
															<tbody>
																{filteredCombineCandidates.length === 0 && (
																	<tr>
																		<td colSpan={5} className="p-3 text-center text-gray-500">
																			No eligible classes found.
																		</td>
																	</tr>
																)}

																{filteredCombineCandidates
																	.sort((a, b) => {
																		const selectedEmpIds = new Set(
																			(selectedClass?.roles || []).map((r) => String(r.empid)),
																		);
																		const aEmpIds = (a.roles || []).map((r) => String(r.empid));
																		const bEmpIds = (b.roles || []).map((r) => String(r.empid));

																		const aShared = aEmpIds.filter((id) =>
																			selectedEmpIds.has(id),
																		).length;
																		const bShared = bEmpIds.filter((id) =>
																			selectedEmpIds.has(id),
																		).length;

																		// Sort by shared instructors descending, then by classid ascending
																		if (bShared !== aShared) return bShared - aShared;
																		return a.classid - b.classid;
																	})
																	.map((ci) => {
																		const isSelected = combineSelection.includes(ci.classid);

																		const disabled = !ci.csize || ci.csize <= 0;

																		return (
																			<tr
																				key={ci.classid}
																				onClick={(e) => {
																					e.preventDefault();
																					if (!disabled) toggleCombineClass(ci.classid);
																				}}
																				className={`border-t ${
																					disabled
																						? "opacity-50 cursor-not-allowed"
																						: "hover:bg-gray-50"
																				}`}
																			>
																				<td className="p-2">
																					<input
																						type="checkbox"
																						disabled={disabled}
																						checked={isSelected}
																					/>
																				</td>
																				<td className="p-2">{ci.classid}</td>

																				<td className="p-2 truncate" title={ci.ctitle}>
																					{ci.ctitle}
																				</td>
																				<td className="p-2 text-right">{ci.csize ?? "-"}</td>
																				<td className="p-2 text-right">
																					{ci.roles.map((role) => role.name).join(" | ")}
																				</td>
																			</tr>
																		);
																	})}
															</tbody>
														</table>
													</div>
												</div>
											</div>

											<div className="flex justify-end gap-2 ">
												<Button variant="outlined" onClick={() => setExpanded(null)}>
													Cancel
												</Button>
												<Button
													variant="contained"
													onClick={handleApplyCombine}
													disabled={combineSelection.length === 0}
												>
													Apply Combine
												</Button>
											</div>

											<p className="text-xs text-gray-600 mt-1">
												Note: The combined class size will be updated automatically. You can
												manually adjust the course title (e.g., CHEM 101/401 LEC A1) in the fields
												above.
											</p>
										</div>
									)}

									{/* If pre-combined from SIS, we show info but lock editing */}
									{expanded === "combines" && selectedClass?.csPreCombined && (
										<p className="mt-3 text-sm text-amber-700">
											This class is pre-combined from BearTracks (SIS). Combine settings cannot be
											edited here.
										</p>
									)}
								</AccordionDetails>
							</Accordion>
						</div>
						<Box
							sx={{
								width: "100%",

								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "end",
								gap: "10px",
							}}
						>
							{/* Left side - Change indicator */}
							<div className="flex items-center gap-2">
								{hasChanges && (
									<span className="text-amber-600 text-sm font-medium">● Unsaved changes</span>
								)}
							</div>

							{/* Right side - Action buttons */}
							<div className="flex gap-2 pt-2">
								{hasChanges && (
									<Button
										variant="outlined"
										color="warning"
										disabled={postClassIdsRolesMutation.isLoading}
										sx={{
											width: "140px",
										}}
										onClick={handleDiscardChanges}
									>
										Discard Changes
									</Button>
								)}
								<Button
									variant="contained"
									color="error"
									disabled={postClassIdsRolesMutation.isLoading}
									sx={{
										width: "100px",
									}}
									onClick={handleClose}
								>
									{hasChanges ? "Cancel" : "Close"}
								</Button>
								{postClassIdsRolesMutation.isLoading ? (
									<Button
										variant="contained"
										sx={{
											width: "100px",
											backgroundColor: "#275d38",
										}}
									>
										<CircularProgress size={20} sx={{ color: "white" }} />
									</Button>
								) : (
									hasChanges && (
										<Button
											variant="contained"
											disabled={loading}
											sx={{
												width: "100px",
												backgroundColor: "#275d38",
												"&:hover": {
													backgroundColor: "#1e4a2c",
												},
											}}
											onClick={handleSaveChanges}
										>
											{loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Save"}
										</Button>
									)
								)}
							</div>
						</Box>
					</div>
				</Box>
			</Modal>

			{/*Add instructor to the role, not push right away.*/}
		</div>
	);
}
